import React, { useState, useEffect } from 'react';
import PageTitle from '../../common/components/PageTitle';
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Stack,
  Paper,
  Grid,
  Link,
} from '@mui/material';
import { styled } from '@mui/system';
import CreateAccountForm from './CreateAccountForm';
import ConfirmAccountForm from './ConfirmAccountForm';
import PickPlanForm from './PickPlanForm';
import BillingInfoForm from './BillingInfoForm';
import DistrictBillingForm from './DistrictBillingForm';
import {
  selectSubscription,
  selectSubscriptionLoaded,
} from '../../common/subscription/SubscriptionSlice';
import { selectIsLoggedIn } from '../../common/auth/UserSlice';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { AUTHROOT, EXT_TOS, EXT_PRIVACYPOLICY } from '../../routes/CONSTANTS';
import {
  formatRecurlyPlanInterval,
  formatRecurlyPrice,
} from '../../common/utils/formatFunctions';
import { RecurlyProvider, Elements } from '@recurly/react-recurly';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Link as RouterLink } from 'react-router-dom';
import { handleValidateReferral } from '../../common/subscription/SubscriptionService';
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import WarnIcon from '@mui/icons-material/WarningAmber';
import { EXT_REF_TOS, SIGNUP } from '../../routes/CONSTANTS';
import useMediaQuery from '@mui/material/useMediaQuery';

const steps = [
  'Create Account',
  'Confirm Email',
  'Pick a Plan',
  'Enter Billing Info',
];

const Item = styled(Paper)(({ theme }) => ({
  boxShadow: 'none',
  borderRadius: 0,
  margin: theme.spacing(0.5),
}));

const LeftItem = styled(Paper)(({ theme }) => ({
  boxShadow: 'none',
  borderRadius: 0,
  margin: theme.spacing(0.5),
  width: '98%',
  textAlign: 'left',
}));

export function SignUp() {
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedPlan, setSelecetedPlan] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const { referrer } = useParams();

  const subscriptionLoaded = useSelector(selectSubscriptionLoaded);
  const subscriptionInfo = useSelector(selectSubscription);
  const isLoggedIn = useSelector(selectIsLoggedIn);

  const referrerId = referrer ? referrer : '';
  const [referralValid, setReferralValid] = useState(true);

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));

  useEffect(() => {
    if (subscriptionLoaded) {
      if (isLoggedIn) {
        //If a user is logged in and has a subscription, they should go to the member home page
        if (subscriptionInfo.SubscriptionID) {
          navigate(AUTHROOT);
        }
        //If a user is logged in but doesn't have a subscription, they need to go to step 2
        else if (activeStep < 2) {
          setActiveStep(2);
        }
      }
      //Not sure if this is still needed here, need more extensive testing before removing
      else if (location.state && location.state.activeStep > 0) {
        setActiveStep(location.state.activeStep);
        location.state.email && setEmail(location.state.email);
        location.state.password && setPassword(location.state.password);
      }
    } else if (location.state && location.state.activeStep > 0) {
      setActiveStep(location.state.activeStep);
      location.state.email && setEmail(location.state.email);
      location.state.password && setPassword(location.state.password);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionLoaded]);

  useEffect(() => {
    if (referrerId) {
      handleValidateReferral(referrerId).then((res) => {
        if (res) {
          setReferralValid(true);
        } else {
          setReferralValid(false);
        }
      });
    }
  }, [referrerId]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSetEmail = (email) => {
    setEmail(email);
  };

  const handleSetPassword = (password) => {
    setPassword(password);
  };

  const handleSetSelectedPlan = (plan) => {
    setSelecetedPlan(plan);
  };

  return (
    <>
      <PageTitle title='Sign Up' />
      <Stack
        direction='column'
        alignItems='center'
        justifyContent='center'
        spacing={2}
      >
        {referrerId && referralValid && (
          <Typography>
            <CheckIcon
              sx={{ color: 'success.main' }}
              style={{ verticalAlign: 'middle' }}
            />
            You are using a referral link. Please see these{' '}
            <Link href={EXT_REF_TOS} target='_blank' rel='noreferrer'>
              Terms and Conditions
            </Link>{' '}
            that apply to the referral processs and note that your discount will
            be applied as a coupon after you subscribe.
          </Typography>
        )}
        <Stepper
          activeStep={activeStep}
          sx={{ paddingTop: '40px', paddingBottom: '40px', width: '70%' }}
          {...(isSmallScreen
            ? { orientation: 'vertical' }
            : { orientation: 'horizontal' })}
        >
          {steps.map((label) => {
            const stepProps = {};
            const labelProps = {};
            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
        {referralValid === false ? (
          <Typography variant='h5'>
            <WarnIcon
              sx={{ color: 'warning.main' }}
              style={{ verticalAlign: 'middle' }}
            />
            You are using an invalid referral link, you will not be able to
            complete the sign up process with this link. If you would like to
            sign up without the referral benefit, please{' '}
            <Link href={SIGNUP} target='_self'>
              click here
            </Link>{' '}
            . Otherwise, please contact the person that provided the referral
            link to verify it, and note that there are certain{' '}
            <Link href={EXT_REF_TOS} target='_blank' rel='noreferrer'>
              terms and conditions
            </Link>{' '}
            that apply to the referral that may not be met.
          </Typography>
        ) : activeStep === 0 ? (
          <>
            <Typography variant='h5'>
              Create a LINKtivity® Learning Account
            </Typography>
            <CreateAccountForm
              setEmail={handleSetEmail}
              setPassword={handleSetPassword}
              successAction={handleNext}
            />
          </>
        ) : activeStep === 1 ? (
          <>
            <Typography variant='h5'>Confirm Your Email Address</Typography>
            <ConfirmAccountForm
              email={email}
              password={password}
              successAction={handleNext}
            />
          </>
        ) : activeStep === 2 ? (
          <>
            <Typography variant='h5'>Pick a Subscription Plan</Typography>
            <PickPlanForm
              setSelecetedPlan={handleSetSelectedPlan}
              successAction={handleNext}
            />
          </>
        ) : activeStep === 3 ? (
          selectedPlan.PlanCode ? (
            selectedPlan.PlanCode === 'district' ? (
              <>
                <Typography variant='h5'>
                  Enter Sign Up Code and Subscribe
                </Typography>
                <Grid
                  container
                  alignItems='flex-start'
                  justifyContent='center'
                  direction='row'
                  rowSpacing={1}
                  columnSpacing={1}
                >
                  <Grid item xs={12} md={6}>
                    <Stack alignItems='center' justifyContent='center'>
                      <LeftItem>
                        <Typography>
                          <b>Selected Plan: {selectedPlan.PlanName}</b>
                          <br />
                          {selectedPlan.PlanDescription}
                        </Typography>
                      </LeftItem>
                      <LeftItem>
                        <Typography>
                          <b>Plan Cost</b>
                          <br />
                          Pre paid by your district.
                        </Typography>
                      </LeftItem>
                      <Item>
                        <Button
                          color='tertiary'
                          variant='contained'
                          onClick={handleBack}
                        >
                          Select Different Plan
                        </Button>
                      </Item>
                      <LeftItem>
                        <Typography variant='subtitle2'>
                          By subscribing and continuing to use this website you
                          are agreeing to the{' '}
                          <Link href={EXT_TOS} target='_blank' rel='noreferrer'>
                            Terms of Service
                          </Link>{' '}
                          and the{' '}
                          <Link
                            href={EXT_PRIVACYPOLICY}
                            target='_blank'
                            rel='noreferrer'
                          >
                            Privacy Policy
                          </Link>
                          .
                        </Typography>
                      </LeftItem>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DistrictBillingForm
                      planCode={selectedPlan.PlanCode}
                      successAction={() => {
                        handleNext();
                        navigate(AUTHROOT);
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            ) : (
              <>
                <Typography variant='h5'>
                  Enter Billing Info and Subscribe
                </Typography>
                <Grid
                  container
                  alignItems='flex-start'
                  justifyContent='center'
                  direction='row'
                  rowSpacing={1}
                  columnSpacing={1}
                >
                  <Grid item xs={12} md={6}>
                    <Stack alignItems='center' justifyContent='center'>
                      <LeftItem>
                        <Typography>
                          <b>Selected Plan: {selectedPlan.PlanName}</b>
                          <br />
                          {selectedPlan.PlanDescription}
                        </Typography>
                      </LeftItem>
                      {selectedPlan.FreeTrialLength > 0 && (
                        <LeftItem>
                          <Typography>
                            <b>Free Trial Length</b>
                            <br />
                            {selectedPlan.FreeTrialLength +
                              ' ' +
                              selectedPlan.FreeTrialInterval}
                          </Typography>
                        </LeftItem>
                      )}
                      <LeftItem>
                        <Typography>
                          <b>
                            Plan Cost
                            {selectedPlan.FreeTrialLength > 0 &&
                              ' After Free Trial'}
                          </b>
                          <br />
                          {formatRecurlyPrice(
                            selectedPlan.PlanUnitCost,
                            selectedPlan.PlanCurrencyCode
                          ) +
                            ' ' +
                            formatRecurlyPlanInterval(
                              selectedPlan.PlanInterval,
                              selectedPlan.PlanIntervalLength
                            ) +
                            ' (does not reflect coupons or discounts)'}
                        </Typography>
                      </LeftItem>
                      <Item>
                        <Button
                          color='tertiary'
                          variant='contained'
                          onClick={handleBack}
                        >
                          Select Different Plan
                        </Button>
                      </Item>
                      <LeftItem>
                        <Typography variant='subtitle2'>
                          {selectedPlan.FreeTrialLength > 0
                            ? ` This plan comes with a ` +
                              selectedPlan.FreeTrialLength +
                              ` ` +
                              selectedPlan.FreeTrialInterval.replace(
                                /s+$/,
                                ''
                              ) +
                              ` free trial. When you click subscribe, the credit card you entered will be verified and your free trial will begin. 
                        After the free trial, this credit card will be automatically charged on a recurring basis per the terms of the plan unless you cancel 
                        the subscription. You are authorizing and agreeing to pay these charges by continuing.`
                            : ` When you click subscribe, the credit card you entered will be charged for the cost of the plan and your subscription will begin. 
                        the credit card entered here will be automatically charged on a recurring basis per the terms of the plan unless you cancel the subscription. 
                        You are authorizing and agreeing to pay these charges by continuing.`}
                        </Typography>
                        <Typography variant='subtitle2'>
                          By subscribing and continuing to use this website you
                          are agreeing to the{' '}
                          <Link href={EXT_TOS} target='_blank' rel='noreferrer'>
                            Terms of Service
                          </Link>{' '}
                          and the{' '}
                          <Link
                            href={EXT_PRIVACYPOLICY}
                            target='_blank'
                            rel='noreferrer'
                          >
                            Privacy Policy
                          </Link>
                          .
                        </Typography>
                      </LeftItem>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RecurlyProvider
                      publicKey={process.env.REACT_APP_RECURLY_PUBLIC_API_KEY}
                    >
                      <Elements>
                        <BillingInfoForm
                          planCode={selectedPlan.PlanCode}
                          referrerId={referrerId}
                          successAction={() => {
                            window.dataLayer.push({
                              event: 'subscribe',
                            });
                            handleNext();
                            navigate(AUTHROOT);
                          }}
                        />
                      </Elements>
                    </RecurlyProvider>
                  </Grid>
                </Grid>
              </>
            )
          ) : (
            <>
              <Typography sx={{ color: 'error.main' }}>
                No plan selected
              </Typography>
            </>
          )
        ) : (
          <>
            <Typography variant='h5' sx={{ color: 'success.main' }}>
              <CheckCircleOutlineIcon />
              Success!
            </Typography>
            <Typography>
              Thank you for subscribing. Head over to the{' '}
              <Link component={RouterLink} to={AUTHROOT}>
                Member Home
              </Link>{' '}
              to start exploring now!
            </Typography>
          </>
        )}
      </Stack>
    </>
  );
}
